import React, { useState, useEffect } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { EasyCheckButton, EasyCheckDialog, EasyCheckDataGrid } from '../../common/Controls';
import { chequeService } from '../../../services/cheques/chequeService';
import { formatDateForDisplay, formatDateForAPI } from '../../../utils/dateUtils';
import { formatCurrencyAmount } from '../../../utils/currencyUtils';

const makeColumns = (currency = 'AED') => [
  {
    field: 'chequeNumber',
    headerName: 'Cheque #',
    width: 130,
    valueGetter: (value, row) => row.ChequeNumber || row.chequeNumber || '-',
    renderCell: (params) => (
      <Box sx={{ fontFamily: 'monospace', fontWeight: 500 }}>{params.value}</Box>
    ),
  },
  {
    field: 'beneficiaryName',
    headerName: 'Beneficiary',
    flex: 1.5,
    minWidth: 180,
    valueGetter: (value, row) => row.BeneficiaryName || row.beneficiaryName || '',
  },
  {
    field: 'chqAmount',
    headerName: 'Amount',
    width: 130,
    type: 'number',
    align: 'right',
    headerAlign: 'right',
    valueGetter: (value, row) => row.ChqAmount || row.chqAmount || 0,
    valueFormatter: (value) => {
      if (!value) return '';
      return formatCurrencyAmount(value, currency, { withSymbol: false });
    },
  },
  {
    field: 'chqDate',
    headerName: 'Date',
    width: 120,
    valueGetter: (value, row) => row.ChqDate || row.chqDate || '',
    renderCell: (params) => {
      if (!params.value) return '-';
      return formatDateForDisplay(params.value);
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 100,
    valueGetter: (value, row) => row.Status || row.status || '',
  },
];

const CopyFromExistingDialog = ({ open, onClose, onCopyComplete, bankId, currency = 'AED' }) => {
  const columns = React.useMemo(() => makeColumns(currency), [currency]);
  const [cheques, setCheques] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && bankId) {
      setLoading(true);
      setError(null);
      setSelectedIds([]);
      chequeService.getPrintedCheques({ bankId, pageSize: 10000, status: '' })
        .then(result => {
          if (result.success) {
            const data = Array.isArray(result.data) ? result.data : [];
            // Add id field for DataGrid if not present
            const withIds = data.map((row, i) => ({
              ...row,
              id: row.Id || row.id || row.ChequeId || row.chequeId || i,
            }));
            setCheques(withIds);
          } else {
            setError(result.message || 'Failed to load cheques');
          }
        })
        .catch(err => setError(err.message))
        .finally(() => setLoading(false));
    }
  }, [open, bankId]);

  const handleCopy = () => {
    const selected = cheques.filter(c => selectedIds.includes(c.id));
    const today = formatDateForAPI(new Date());

    const rows = selected.map(c => ({
      beneficiaryName: c.BeneficiaryName || c.beneficiaryName || '',
      chqAmount: parseFloat(c.ChqAmount || c.chqAmount || 0),
      chqDate: today,
      chequeNumber: '',
      remarks: `Copied from cheque ${c.ChequeNumber || c.chequeNumber || ''}`.trim(),
      source: 'copy',
    }));

    onCopyComplete(rows);
    onClose();
  };

  const handleClose = () => {
    setSelectedIds([]);
    setCheques([]);
    onClose();
  };

  return (
    <EasyCheckDialog
      open={open}
      onClose={handleClose}
      title="Copy from Existing Cheques"
      titleIcon={<CopyIcon />}
      variant="large"
      maxWidth="lg"
      actions={
        <>
          <EasyCheckButton buttonType="form" variant="outlined" onClick={handleClose}>
            Cancel
          </EasyCheckButton>
          <EasyCheckButton
            buttonType="form"
            onClick={handleCopy}
            disabled={selectedIds.length === 0}
          >
            Copy Selected ({selectedIds.length})
          </EasyCheckButton>
        </>
      }
    >
      <Box sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select cheques to duplicate. Copied cheques will use today's date and empty cheque numbers.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ height: 450 }}>
          <EasyCheckDataGrid
            rows={cheques}
            columns={columns}
            loading={loading}
            checkboxSelection
            rowSelectionModel={selectedIds}
            onRowSelectionModelChange={(newSelection) => setSelectedIds(newSelection)}
            pageSize={50}
            rowsPerPageOptions={[25, 50, 100]}
            sx={{ height: '100%' }}
          />
        </Box>
      </Box>
    </EasyCheckDialog>
  );
};

export default CopyFromExistingDialog;
